import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  QualificationCriteria, CompanyProfile, PastPerformance,
  ScoringWeight, Gate1Config,
} from "../adminTypes";
import {
  getCriteria, updateCriteria,
  getProfile, updateProfile,
  getPastPerformance, addPastPerformance, updatePastPerformance, deletePastPerformance,
} from "../adminApi";

type Tab = "scoring" | "criteria" | "profile" | "past-performance";

function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<Tab>("scoring");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [criteria, setCriteria] = useState<QualificationCriteria | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [pastPerf, setPastPerf] = useState<PastPerformance[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [c, p, pp] = await Promise.all([
        getCriteria(),
        getProfile(),
        getPastPerformance(),
      ]);
      setCriteria(c);
      setProfile(p);
      setPastPerf(pp.records);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load admin data. You may not have admin access.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={centerStyle}>
        <p style={{ color: "#666" }}>Loading admin panel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={centerStyle}>
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "#c0392b" }}>{error}</p>
          <button onClick={() => navigate("/")} style={linkBtnStyle}>Back to Queue</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f0f2f5", fontFamily: "Segoe UI, sans-serif" }}>
      {/* Header */}
      <div style={{
        backgroundColor: "#1a5276", color: "white", padding: "12px 24px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button onClick={() => navigate("/")} style={{
            background: "none", border: "1px solid rgba(255,255,255,0.3)", color: "white",
            padding: "4px 12px", borderRadius: "4px", cursor: "pointer", fontSize: "13px",
          }}>
            Back
          </button>
          <h1 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Admin Panel</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "12px" }}>
          <span>{user?.name}</span>
          <button onClick={logout} style={{
            padding: "4px 10px", backgroundColor: "rgba(255,255,255,0.15)",
            color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "4px",
            cursor: "pointer", fontSize: "11px",
          }}>Sign out</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        maxWidth: "960px", margin: "0 auto", padding: "24px 24px 0",
        display: "flex", gap: "4px", borderBottom: "1px solid #e0e0e0",
      }}>
        {(
          [
            ["scoring", "Scoring Weights"],
            ["criteria", "Qualification Criteria"],
            ["profile", "Company Profile"],
            ["past-performance", "Past Performance"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: "10px 20px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
              border: "1px solid #e0e0e0", borderBottom: tab === key ? "2px solid #1a5276" : "1px solid #e0e0e0",
              borderRadius: "6px 6px 0 0",
              backgroundColor: tab === key ? "white" : "#f8f9fa",
              color: tab === key ? "#1a5276" : "#666",
              marginBottom: "-1px",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ maxWidth: "960px", margin: "0 auto", padding: "24px" }}>
        {tab === "scoring" && criteria && (
          <ScoringWeightsEditor criteria={criteria} onSaved={load} />
        )}
        {tab === "criteria" && criteria && (
          <CriteriaEditor criteria={criteria} onSaved={load} />
        )}
        {tab === "profile" && profile && (
          <ProfileEditor profile={profile} onSaved={load} />
        )}
        {tab === "past-performance" && (
          <PastPerformanceTab records={pastPerf} onSaved={load} />
        )}
      </div>
    </div>
  );
}

// ── Scoring Weights Tab ─────────────────────────────────────────────────────

function ScoringWeightsEditor({
  criteria,
  onSaved,
}: {
  criteria: QualificationCriteria;
  onSaved: () => void;
}) {
  const [weights, setWeights] = useState<ScoringWeight[]>(
    () => [...criteria.gate2.scoring_weights]
  );
  const [threshold, setThreshold] = useState(criteria.gate2.threshold);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const total = weights.reduce((s, w) => s + w.score_weight, 0);
  const isValid = total === 100;

  const updateWeight = (index: number, value: number) => {
    setWeights((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], score_weight: value };
      return updated;
    });
  };

  const handleSave = async () => {
    if (!isValid) return;
    try {
      setSaving(true);
      setMsg(null);
      await updateCriteria({
        gate2: { threshold, scoring_weights: weights },
      });
      setMsg("Scoring weights saved successfully.");
      onSaved();
    } catch (err: any) {
      setMsg(err?.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={sectionHeading}>Gate 2 Scoring Weights</h3>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 16px" }}>
        Weights must sum to exactly 100. Current total:{" "}
        <strong style={{ color: isValid ? "#27ae60" : "#e74c3c" }}>{total}</strong>/100
      </p>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
            <th style={thStyle}>Factor</th>
            <th style={thStyle}>Label</th>
            <th style={{ ...thStyle, width: "100px", textAlign: "center" }}>Max Points</th>
            <th style={thStyle}>Bar</th>
          </tr>
        </thead>
        <tbody>
          {weights.map((w, i) => (
            <tr key={w.factor} style={{ borderBottom: "1px solid #f0f0f0" }}>
              <td style={tdStyle}>
                <code style={{ fontSize: "11px", backgroundColor: "#f0f2f5", padding: "2px 6px", borderRadius: "3px" }}>
                  {w.factor}
                </code>
              </td>
              <td style={tdStyle}>{w.label}</td>
              <td style={{ ...tdStyle, textAlign: "center" }}>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={w.score_weight}
                  onChange={(e) => updateWeight(i, parseInt(e.target.value) || 0)}
                  style={{
                    width: "60px", padding: "4px 8px", border: "1px solid #ddd",
                    borderRadius: "4px", textAlign: "center", fontSize: "13px",
                  }}
                />
              </td>
              <td style={tdStyle}>
                <div style={{
                  width: `${w.score_weight}%`, height: "16px", backgroundColor: "#1a5276",
                  borderRadius: "3px", minWidth: w.score_weight > 0 ? "4px" : "0",
                  transition: "width 0.2s",
                }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: "20px", display: "flex", alignItems: "center", gap: "16px" }}>
        <label style={{ fontSize: "13px", fontWeight: 600, color: "#2c3e50" }}>
          Gate 2 Threshold:
        </label>
        <input
          type="number"
          min={0}
          max={100}
          value={threshold}
          onChange={(e) => setThreshold(parseInt(e.target.value) || 0)}
          style={{
            width: "70px", padding: "6px 10px", border: "1px solid #ddd",
            borderRadius: "4px", textAlign: "center", fontSize: "13px",
          }}
        />
        <span style={{ fontSize: "12px", color: "#999" }}>
          Opps scoring below this skip Gate 3 AI
        </span>
      </div>

      {msg && <div style={{ ...feedbackStyle, color: msg.includes("Failed") ? "#c0392b" : "#27ae60" }}>{msg}</div>}

      <div style={{ marginTop: "16px" }}>
        <button onClick={handleSave} disabled={saving || !isValid} style={{
          ...saveBtnStyle, opacity: saving || !isValid ? 0.6 : 1,
        }}>
          {saving ? "Saving..." : "Save Scoring Weights"}
        </button>
        {!isValid && (
          <span style={{ marginLeft: "12px", fontSize: "12px", color: "#e74c3c" }}>
            Weights must sum to 100 (currently {total})
          </span>
        )}
      </div>
    </div>
  );
}

// ── Qualification Criteria Tab ──────────────────────────────────────────────

function CriteriaEditor({
  criteria,
  onSaved,
}: {
  criteria: QualificationCriteria;
  onSaved: () => void;
}) {
  const [gate1, setGate1] = useState<Gate1Config>(() => ({ ...criteria.gate1 }));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Editable list helpers
  const [newNaics, setNewNaics] = useState("");
  const [newStateIncl, setNewStateIncl] = useState("");
  const [newStateExcl, setNewStateExcl] = useState("");
  const [newKeyword, setNewKeyword] = useState("");

  const addToList = (field: keyof Gate1Config, value: string) => {
    if (!value.trim()) return;
    setGate1((prev) => ({
      ...prev,
      [field]: [...(prev[field] as string[]), value.trim()],
    }));
  };

  const removeFromList = (field: keyof Gate1Config, index: number) => {
    setGate1((prev) => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMsg(null);
      await updateCriteria({ gate1 });
      setMsg("Qualification criteria saved successfully.");
      onSaved();
    } catch (err: any) {
      setMsg(err?.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={sectionHeading}>Gate 1 Qualification Criteria</h3>

      {/* NAICS Codes */}
      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>NAICS Codes Allowed</label>
        <div style={tagContainerStyle}>
          {gate1.naics_codes_allowed.map((code, i) => (
            <span key={i} style={tagStyle}>
              {code}
              <button onClick={() => removeFromList("naics_codes_allowed", i)} style={tagRemoveStyle}>&times;</button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <input
            value={newNaics}
            onChange={(e) => setNewNaics(e.target.value)}
            placeholder="Add NAICS code..."
            style={smallInputStyle}
            onKeyDown={(e) => { if (e.key === "Enter") { addToList("naics_codes_allowed", newNaics); setNewNaics(""); } }}
          />
          <button onClick={() => { addToList("naics_codes_allowed", newNaics); setNewNaics(""); }} style={addBtnStyle}>Add</button>
        </div>
      </div>

      {/* States Included */}
      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>States Included (allowlist — empty = all states)</label>
        <div style={tagContainerStyle}>
          {gate1.states_included.map((st, i) => (
            <span key={i} style={tagStyle}>
              {st}
              <button onClick={() => removeFromList("states_included", i)} style={tagRemoveStyle}>&times;</button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <input
            value={newStateIncl}
            onChange={(e) => setNewStateIncl(e.target.value.toUpperCase())}
            placeholder="Add state (e.g. AZ)..."
            maxLength={2}
            style={{ ...smallInputStyle, width: "100px" }}
            onKeyDown={(e) => { if (e.key === "Enter") { addToList("states_included", newStateIncl); setNewStateIncl(""); } }}
          />
          <button onClick={() => { addToList("states_included", newStateIncl); setNewStateIncl(""); }} style={addBtnStyle}>Add</button>
        </div>
      </div>

      {/* States Excluded */}
      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>States Excluded (blocklist)</label>
        <div style={tagContainerStyle}>
          {gate1.states_excluded.map((st, i) => (
            <span key={i} style={tagStyle}>
              {st}
              <button onClick={() => removeFromList("states_excluded", i)} style={tagRemoveStyle}>&times;</button>
            </span>
          ))}
          {gate1.states_excluded.length === 0 && (
            <span style={{ fontSize: "12px", color: "#999" }}>None</span>
          )}
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <input
            value={newStateExcl}
            onChange={(e) => setNewStateExcl(e.target.value.toUpperCase())}
            placeholder="Add state..."
            maxLength={2}
            style={{ ...smallInputStyle, width: "100px" }}
            onKeyDown={(e) => { if (e.key === "Enter") { addToList("states_excluded", newStateExcl); setNewStateExcl(""); } }}
          />
          <button onClick={() => { addToList("states_excluded", newStateExcl); setNewStateExcl(""); }} style={addBtnStyle}>Add</button>
        </div>
      </div>

      {/* Value Range */}
      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Contract Value Range</label>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <div>
            <span style={{ fontSize: "12px", color: "#999" }}>Min ($)</span>
            <input
              type="number"
              value={gate1.value_min}
              onChange={(e) => setGate1((prev) => ({ ...prev, value_min: parseInt(e.target.value) || 0 }))}
              style={{ ...smallInputStyle, width: "140px", display: "block", marginTop: "4px" }}
            />
          </div>
          <span style={{ marginTop: "16px", color: "#999" }}>to</span>
          <div>
            <span style={{ fontSize: "12px", color: "#999" }}>Max ($)</span>
            <input
              type="number"
              value={gate1.value_max}
              onChange={(e) => setGate1((prev) => ({ ...prev, value_max: parseInt(e.target.value) || 0 }))}
              style={{ ...smallInputStyle, width: "140px", display: "block", marginTop: "4px" }}
            />
          </div>
        </div>
      </div>

      {/* Exclude Keywords */}
      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Exclude Keywords</label>
        <div style={tagContainerStyle}>
          {gate1.exclude_keywords.map((kw, i) => (
            <span key={i} style={tagStyle}>
              {kw}
              <button onClick={() => removeFromList("exclude_keywords", i)} style={tagRemoveStyle}>&times;</button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Add keyword..."
            style={smallInputStyle}
            onKeyDown={(e) => { if (e.key === "Enter") { addToList("exclude_keywords", newKeyword); setNewKeyword(""); } }}
          />
          <button onClick={() => { addToList("exclude_keywords", newKeyword); setNewKeyword(""); }} style={addBtnStyle}>Add</button>
        </div>
      </div>

      {msg && <div style={{ ...feedbackStyle, color: msg.includes("Failed") ? "#c0392b" : "#27ae60" }}>{msg}</div>}

      <button onClick={handleSave} disabled={saving} style={{ ...saveBtnStyle, opacity: saving ? 0.6 : 1 }}>
        {saving ? "Saving..." : "Save Criteria"}
      </button>
    </div>
  );
}

// ── Company Profile Tab ─────────────────────────────────────────────────────

function ProfileEditor({
  profile: initialProfile,
  onSaved,
}: {
  profile: CompanyProfile;
  onSaved: () => void;
}) {
  const [p, setP] = useState<CompanyProfile>(() => ({ ...initialProfile }));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const setField = (field: string, value: any) => {
    setP((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMsg(null);
      const { _id, config_key, created_at, ...data } = p; // eslint-disable-line @typescript-eslint/no-unused-vars
      await updateProfile(data);
      setMsg("Profile saved successfully.");
      onSaved();
    } catch (err: any) {
      setMsg(err?.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={sectionHeading}>Company Profile</h3>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Company Name</label>
        <input value={p.company_name} onChange={(e) => setField("company_name", e.target.value)} style={inputStyle} />
      </div>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>DBA Name</label>
        <input value={p.dba_name || ""} onChange={(e) => setField("dba_name", e.target.value || null)} style={inputStyle} />
      </div>

      <div style={{ display: "flex", gap: "16px" }}>
        <div style={{ ...fieldGroupStyle, flex: 1 }}>
          <label style={fieldLabel}>CAGE Code</label>
          <input value={p.cage_code || ""} onChange={(e) => setField("cage_code", e.target.value || null)} style={inputStyle} />
        </div>
        <div style={{ ...fieldGroupStyle, flex: 1 }}>
          <label style={fieldLabel}>UEI</label>
          <input value={p.uei || ""} onChange={(e) => setField("uei", e.target.value || null)} style={inputStyle} />
        </div>
        <div style={{ ...fieldGroupStyle, flex: 1 }}>
          <label style={fieldLabel}>Primary NAICS</label>
          <input value={p.primary_naics} onChange={(e) => setField("primary_naics", e.target.value)} style={inputStyle} />
        </div>
      </div>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Website</label>
        <input value={p.website} onChange={(e) => setField("website", e.target.value)} style={inputStyle} />
      </div>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Company Description</label>
        <textarea
          value={p.company_description}
          onChange={(e) => setField("company_description", e.target.value)}
          rows={4}
          style={textareaStyle}
        />
      </div>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Past Performance Summary</label>
        <textarea
          value={p.past_performance_summary}
          onChange={(e) => setField("past_performance_summary", e.target.value)}
          rows={4}
          style={textareaStyle}
        />
      </div>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Capabilities (comma-separated)</label>
        <textarea
          value={p.capabilities.join(", ")}
          onChange={(e) => setField("capabilities", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          rows={3}
          style={textareaStyle}
        />
      </div>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Certifications (comma-separated)</label>
        <input
          value={p.certifications.join(", ")}
          onChange={(e) => setField("certifications", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          style={inputStyle}
        />
      </div>

      <div style={{ display: "flex", gap: "16px" }}>
        <div style={{ ...fieldGroupStyle, flex: 1 }}>
          <label style={fieldLabel}>Annual Revenue ($)</label>
          <input
            type="number"
            value={p.annual_revenue || ""}
            onChange={(e) => setField("annual_revenue", e.target.value ? parseInt(e.target.value) : null)}
            style={inputStyle}
          />
        </div>
        <div style={{ ...fieldGroupStyle, flex: 1 }}>
          <label style={fieldLabel}>Years in Business</label>
          <input
            type="number"
            value={p.years_in_business || ""}
            onChange={(e) => setField("years_in_business", e.target.value ? parseInt(e.target.value) : null)}
            style={inputStyle}
          />
        </div>
        <div style={{ ...fieldGroupStyle, flex: 1 }}>
          <label style={fieldLabel}>Total Employees</label>
          <input
            type="number"
            value={p.employees?.total || ""}
            onChange={(e) => setField("employees", {
              ...p.employees,
              total: e.target.value ? parseInt(e.target.value) : null,
            })}
            style={inputStyle}
          />
        </div>
      </div>

      <div style={fieldGroupStyle}>
        <label style={fieldLabel}>Geographic Coverage — States (comma-separated)</label>
        <input
          value={p.geographic_coverage?.states?.join(", ") || ""}
          onChange={(e) => setField("geographic_coverage", {
            ...p.geographic_coverage,
            states: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
          })}
          style={inputStyle}
        />
      </div>

      {/* ── Branding ────────────────────────────────────────────────────── */}
      <div style={{
        borderTop: "1px solid #e0e0e0", marginTop: "8px", paddingTop: "20px", marginBottom: "16px",
      }}>
        <h4 style={{ margin: "0 0 4px", fontSize: "14px", color: "#2c3e50", fontWeight: 600 }}>
          Branding
        </h4>
        <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#888" }}>
          Enter full image URLs (https://…). Leave blank to use the default placeholder.
        </p>

        <div style={fieldGroupStyle}>
          <label style={fieldLabel}>Header Logo URL</label>
          <input
            value={p.header_logo_url || ""}
            onChange={(e) => setField("header_logo_url", e.target.value || null)}
            placeholder="https://example.com/logo.png"
            style={inputStyle}
          />
          {p.header_logo_url && (
            <div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#1a5276", borderRadius: "4px", display: "inline-block" }}>
              <img
                src={p.header_logo_url}
                alt="Header logo preview"
                style={{ maxHeight: "40px", maxWidth: "200px", display: "block" }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            </div>
          )}
        </div>

        <div style={fieldGroupStyle}>
          <label style={fieldLabel}>Login Screen Logo URL</label>
          <input
            value={p.login_logo_url || ""}
            onChange={(e) => setField("login_logo_url", e.target.value || null)}
            placeholder="https://example.com/logo.png"
            style={inputStyle}
          />
          {p.login_logo_url && (
            <div style={{ marginTop: "8px", padding: "16px", backgroundColor: "#f0f2f5", borderRadius: "4px", display: "inline-block" }}>
              <img
                src={p.login_logo_url}
                alt="Login logo preview"
                style={{ maxHeight: "80px", maxWidth: "200px", display: "block" }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            </div>
          )}
        </div>
      </div>

      {msg && <div style={{ ...feedbackStyle, color: msg.includes("Failed") ? "#c0392b" : "#27ae60" }}>{msg}</div>}

      <button onClick={handleSave} disabled={saving} style={{ ...saveBtnStyle, opacity: saving ? 0.6 : 1 }}>
        {saving ? "Saving..." : "Save Profile"}
      </button>
    </div>
  );
}

// ── Past Performance Tab ────────────────────────────────────────────────────

function PastPerformanceTab({
  records,
  onSaved,
}: {
  records: PastPerformance[];
  onSaved: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // New record form
  const empty = {
    contract_name: "",
    agency: "",
    contract_number: null as string | null,
    naics_code: "",
    description: "",
    value: 0,
    period_of_performance: { start: "", end: "" },
    place_of_performance: "",
    key_personnel: [] as string[],
    rating: null as string | null,
    relevance_tags: [] as string[],
  };
  const [form, setForm] = useState(empty);
  const [tagsInput, setTagsInput] = useState("");
  const [personnelInput, setPersonnelInput] = useState("");

  const resetForm = () => {
    setForm(empty);
    setTagsInput("");
    setPersonnelInput("");
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (r: PastPerformance) => {
    setEditingId(r._id);
    setForm({
      contract_name: r.contract_name,
      agency: r.agency,
      contract_number: r.contract_number,
      naics_code: r.naics_code,
      description: r.description,
      value: r.value,
      period_of_performance: { start: r.period_of_performance?.start || "", end: r.period_of_performance?.end || "" },
      place_of_performance: r.place_of_performance,
      key_personnel: r.key_personnel,
      rating: r.rating,
      relevance_tags: r.relevance_tags,
    });
    setTagsInput(r.relevance_tags.join(", "));
    setPersonnelInput(r.key_personnel.join(", "));
    setShowForm(true);
    setMsg(null);
  };

  const handleSave = async () => {
    if (!form.contract_name || !form.agency) {
      setMsg("Contract name and agency are required.");
      return;
    }
    try {
      setSaving(true);
      setMsg(null);
      const payload = {
        ...form,
        key_personnel: personnelInput.split(",").map((s) => s.trim()).filter(Boolean),
        relevance_tags: tagsInput.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await updatePastPerformance(editingId, payload);
        setMsg("Record updated.");
      } else {
        await addPastPerformance(payload);
        setMsg("Past performance record added.");
      }
      resetForm();
      onSaved();
    } catch (err: any) {
      setMsg(err?.response?.data?.error || "Failed to save record");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setDeleting(true);
      setMsg(null);
      await deletePastPerformance(id);
      setMsg("Record deleted.");
      setConfirmDeleteId(null);
      onSaved();
    } catch (err: any) {
      setMsg(err?.response?.data?.error || "Failed to delete record");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ ...sectionHeading, margin: 0 }}>Past Performance ({records.length} records)</h3>
        <button onClick={() => { if (showForm) { resetForm(); } else { setShowForm(true); } }} style={{
          padding: "6px 14px", backgroundColor: "#1a5276", color: "white",
          border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: 600,
        }}>
          {showForm ? "Cancel" : "+ Add Record"}
        </button>
      </div>

      {showForm && (
        <div style={{
          backgroundColor: "#f8f9fa", borderRadius: "6px", padding: "16px",
          marginBottom: "16px", border: editingId ? "2px solid #2980b9" : "1px solid #e0e0e0",
        }}>
          {editingId && (
            <div style={{ fontSize: "12px", color: "#2980b9", fontWeight: 600, marginBottom: "8px" }}>
              Editing record...
            </div>
          )}
          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ ...fieldGroupStyle, flex: 2 }}>
              <label style={fieldLabel}>Contract Name *</label>
              <input value={form.contract_name} onChange={(e) => setForm({ ...form, contract_name: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>Agency *</label>
              <input value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} style={inputStyle} />
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>Contract Number</label>
              <input value={form.contract_number || ""} onChange={(e) => setForm({ ...form, contract_number: e.target.value || null })} style={inputStyle} />
            </div>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>NAICS Code</label>
              <input value={form.naics_code} onChange={(e) => setForm({ ...form, naics_code: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>Value ($)</label>
              <input type="number" value={form.value || ""} onChange={(e) => setForm({ ...form, value: parseInt(e.target.value) || 0 })} style={inputStyle} />
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={fieldLabel}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} style={textareaStyle} />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>Place of Performance</label>
              <input value={form.place_of_performance} onChange={(e) => setForm({ ...form, place_of_performance: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>CPARS Rating</label>
              <select value={form.rating || ""} onChange={(e) => setForm({ ...form, rating: e.target.value || null })} style={inputStyle}>
                <option value="">N/A</option>
                <option value="Exceptional">Exceptional</option>
                <option value="Very Good">Very Good</option>
                <option value="Satisfactory">Satisfactory</option>
                <option value="Marginal">Marginal</option>
                <option value="Unsatisfactory">Unsatisfactory</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>Period Start (YYYY-MM-DD)</label>
              <input type="date" value={form.period_of_performance.start} onChange={(e) => setForm({ ...form, period_of_performance: { ...form.period_of_performance, start: e.target.value } })} style={inputStyle} />
            </div>
            <div style={{ ...fieldGroupStyle, flex: 1 }}>
              <label style={fieldLabel}>Period End (YYYY-MM-DD)</label>
              <input type="date" value={form.period_of_performance.end} onChange={(e) => setForm({ ...form, period_of_performance: { ...form.period_of_performance, end: e.target.value } })} style={inputStyle} />
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={fieldLabel}>Key Personnel (comma-separated)</label>
            <input value={personnelInput} onChange={(e) => setPersonnelInput(e.target.value)} style={inputStyle} placeholder="John Doe, Jane Smith" />
          </div>

          <div style={fieldGroupStyle}>
            <label style={fieldLabel}>Relevance Tags (comma-separated)</label>
            <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} style={inputStyle} placeholder="janitorial, federal, facilities" />
          </div>

          <button onClick={handleSave} disabled={saving} style={{ ...saveBtnStyle, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving..." : editingId ? "Update Record" : "Add Record"}
          </button>
        </div>
      )}

      {msg && <div style={{ ...feedbackStyle, color: msg.includes("Failed") || msg.includes("required") ? "#c0392b" : "#27ae60" }}>{msg}</div>}

      {/* Records list */}
      {records.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#999", textAlign: "center", padding: "24px" }}>
          No past performance records yet.
        </p>
      ) : (
        records.map((r) => (
          <div key={r._id} style={{
            padding: "14px", marginBottom: "8px", backgroundColor: "#f8f9fa",
            borderRadius: "6px", border: "1px solid #e0e0e0", fontSize: "13px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontWeight: 600, color: "#2c3e50" }}>{r.contract_name}</div>
                <div style={{ color: "#666", fontSize: "12px", marginTop: "2px" }}>
                  {r.agency} | NAICS: {r.naics_code} | ${Number(r.value).toLocaleString()}
                </div>
              </div>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                {r.rating && (
                  <span style={{
                    padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 600,
                    backgroundColor: "#d4edda", color: "#155724",
                  }}>
                    {r.rating}
                  </span>
                )}
                <button onClick={() => startEdit(r)} style={{
                  padding: "3px 10px", fontSize: "11px", fontWeight: 600,
                  backgroundColor: "#2980b9", color: "white", border: "none",
                  borderRadius: "4px", cursor: "pointer",
                }}>
                  Edit
                </button>
                {confirmDeleteId === r._id ? (
                  <span style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <span style={{ fontSize: "11px", color: "#c0392b", fontWeight: 600 }}>Delete?</span>
                    <button onClick={() => handleDelete(r._id)} disabled={deleting} style={{
                      padding: "3px 8px", fontSize: "11px", fontWeight: 600,
                      backgroundColor: "#c0392b", color: "white", border: "none",
                      borderRadius: "4px", cursor: "pointer", opacity: deleting ? 0.6 : 1,
                    }}>
                      {deleting ? "..." : "Yes"}
                    </button>
                    <button onClick={() => setConfirmDeleteId(null)} style={{
                      padding: "3px 8px", fontSize: "11px", fontWeight: 600,
                      backgroundColor: "#7f8c8d", color: "white", border: "none",
                      borderRadius: "4px", cursor: "pointer",
                    }}>
                      No
                    </button>
                  </span>
                ) : (
                  <button onClick={() => setConfirmDeleteId(r._id)} style={{
                    padding: "3px 10px", fontSize: "11px", fontWeight: 600,
                    backgroundColor: "#e74c3c", color: "white", border: "none",
                    borderRadius: "4px", cursor: "pointer",
                  }}>
                    Delete
                  </button>
                )}
              </div>
            </div>
            {r.description && (
              <div style={{ color: "#444", marginTop: "6px", lineHeight: 1.5 }}>
                {r.description.substring(0, 200)}{r.description.length > 200 ? "..." : ""}
              </div>
            )}
            {r.relevance_tags.length > 0 && (
              <div style={{ marginTop: "6px", display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {r.relevance_tags.map((tag, i) => (
                  <span key={i} style={{
                    padding: "1px 8px", borderRadius: "10px", fontSize: "10px",
                    backgroundColor: "#e8f4fd", color: "#1a5276",
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ── Shared Styles ───────────────────────────────────────────────────────────

const centerStyle: React.CSSProperties = {
  minHeight: "100vh", display: "flex", alignItems: "center",
  justifyContent: "center", backgroundColor: "#f0f2f5",
  fontFamily: "Segoe UI, sans-serif",
};

const linkBtnStyle: React.CSSProperties = {
  padding: "8px 16px", backgroundColor: "#1a5276", color: "white",
  border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: "white", borderRadius: "8px", border: "1px solid #e0e0e0",
  padding: "24px",
};

const sectionHeading: React.CSSProperties = {
  margin: "0 0 16px", fontSize: "16px", color: "#2c3e50", fontWeight: 600,
};

const fieldGroupStyle: React.CSSProperties = {
  marginBottom: "16px",
};

const fieldLabel: React.CSSProperties = {
  display: "block", fontSize: "12px", fontWeight: 600, color: "#666",
  marginBottom: "4px",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 12px", border: "1px solid #ddd",
  borderRadius: "4px", fontSize: "13px", boxSizing: "border-box",
  fontFamily: "Segoe UI, sans-serif",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: "vertical", lineHeight: 1.6,
};

const smallInputStyle: React.CSSProperties = {
  padding: "6px 10px", border: "1px solid #ddd", borderRadius: "4px",
  fontSize: "13px", width: "180px",
};

const addBtnStyle: React.CSSProperties = {
  padding: "6px 12px", backgroundColor: "#f8f9fa", border: "1px solid #ddd",
  borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: 600,
};

const saveBtnStyle: React.CSSProperties = {
  padding: "10px 24px", backgroundColor: "#27ae60", color: "white",
  border: "none", borderRadius: "4px", cursor: "pointer",
  fontSize: "13px", fontWeight: 600,
};

const feedbackStyle: React.CSSProperties = {
  padding: "10px", borderRadius: "4px", fontSize: "13px",
  marginTop: "12px", marginBottom: "12px",
};

const tagContainerStyle: React.CSSProperties = {
  display: "flex", gap: "6px", flexWrap: "wrap",
};

const tagStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: "4px",
  padding: "3px 10px", borderRadius: "14px", fontSize: "12px",
  backgroundColor: "#e8f4fd", color: "#1a5276",
};

const tagRemoveStyle: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer", fontSize: "14px",
  color: "#999", padding: "0 2px", lineHeight: 1,
};

const thStyle: React.CSSProperties = {
  textAlign: "left", padding: "8px 10px", fontSize: "12px",
  fontWeight: 600, color: "#666",
};

const tdStyle: React.CSSProperties = {
  padding: "10px",
};

export default AdminPanel;
